<template>
  <div ref="spaceContainer" class="space-map-container relative overflow-y-auto overflow-x-hidden">
    <div
      class="space-map-inner relative"
      :style="{ height: totalHeight + 'px' }"
    >
      <!-- Twinkling stars rendered inside inner div so they scroll with content -->
      <div
        v-for="star in bgStars"
        :key="'bg-' + star.id"
        class="absolute rounded-full bg-white twinkle-star"
        :style="{
          left: star.x + '%',
          top: star.y + 'px',
          width: star.size + 'px',
          height: star.size + 'px',
          animationDelay: star.delay + 's',
          animationDuration: star.duration + 's'
        }"
      />

      <!-- Mission Complete banner at top -->
      <div class="absolute -translate-x-1/2 z-10" :style="{ left: lastNodePercent + '%', top: (lastNodeY - 50) + 'px' }">
        <div class="flex items-center gap-2 px-5 py-2 rounded-xl bg-indigo-950/80 shadow-lg border border-indigo-500/30 backdrop-blur-sm">
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <polygon points="11,1 13.5,7.5 20.5,8 15.3,12.5 16.8,19.5 11,15.8 5.2,19.5 6.7,12.5 1.5,8 8.5,7.5" fill="#fbbf24" />
          </svg>
          <span class="text-sm font-bold text-indigo-200 tracking-wide">MISSION COMPLETE</span>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <polygon points="11,1 13.5,7.5 20.5,8 15.3,12.5 16.8,19.5 11,15.8 5.2,19.5 6.7,12.5 1.5,8 8.5,7.5" fill="#fbbf24" />
          </svg>
        </div>
      </div>

      <!-- SVG layer: flight path, nebulae -->
      <svg
        class="absolute inset-0 w-full h-full pointer-events-none"
        :viewBox="`0 0 ${containerWidth} ${totalHeight}`"
      >
        <!-- Nebula clouds -->
        <defs>
          <template v-for="nebula in nebulae" :key="'neb-def-' + nebula.id">
            <radialGradient :id="'neb-grad-' + nebula.id" cx="50%" cy="50%" r="50%">
              <stop offset="0%" :stop-color="nebula.color" stop-opacity="0.25" />
              <stop offset="100%" :stop-color="nebula.color" stop-opacity="0" />
            </radialGradient>
          </template>
        </defs>
        <template v-for="nebula in nebulae" :key="'neb-' + nebula.id">
          <ellipse
            :cx="nebula.x" :cy="nebula.y"
            :rx="nebula.rx" :ry="nebula.ry"
            :fill="`url(#neb-grad-${nebula.id})`"
          />
        </template>

        <!-- Flight path shadow -->
        <path
          v-if="flightPath"
          :d="flightPath"
          stroke="#312e81"
          :stroke-width="4"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          opacity="0.5"
        />

        <!-- Flight path (dotted line) -->
        <path
          v-if="flightPath"
          :d="flightPath"
          stroke="#818cf8"
          :stroke-width="2"
          fill="none"
          stroke-linecap="round"
          stroke-dasharray="8 6"
          opacity="0.7"
        />

        <!-- Completed flight path (solid glowing) -->
        <path
          v-if="completedPath"
          :d="completedPath"
          stroke="#38bdf8"
          :stroke-width="3"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="flight-completed-line"
          :style="{ strokeDasharray: completedPathLength, strokeDashoffset: animatedOffset }"
        />
        <!-- Glow layer for completed path -->
        <path
          v-if="completedPath"
          :d="completedPath"
          stroke="#38bdf8"
          :stroke-width="10"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          opacity="0.15"
          class="flight-completed-line"
          :style="{ strokeDasharray: completedPathLength, strokeDashoffset: animatedOffset }"
        />
      </svg>

      <!-- Constellation markers at unit boundaries -->
      <SpaceConstellation
        v-for="marker in constellationPositions"
        :key="'const-' + marker.unitId"
        :title="marker.title"
        :style="{
          position: 'absolute',
          left: marker.xPercent + '%',
          transform: 'translateX(-50%)',
          top: marker.y + 'px'
        }"
      />

      <!-- Planet / lesson nodes -->
      <div
        v-for="(node, idx) in nodePositions"
        :key="node.lesson.id"
        :style="{
          position: 'absolute',
          left: node.xPercent + '%',
          top: node.y + 'px',
          transform: 'translate(-50%, -50%)'
        }"
      >
        <SpaceNode
          :lesson="node.lesson"
          :index="idx"
          @select="$emit('select-lesson', $event)"
        />
      </div>

      <!-- Rocket at current lesson -->
      <SpaceRocket
        v-if="currentNodePosition"
        :x="currentNodePosition.xPx"
        :y="currentNodePosition.y"
      />

      <!-- Launch pad at bottom -->
      <div
        class="absolute -translate-x-1/2"
        :style="{ left: firstNodePercent + '%', top: (firstNodeY + 40) + 'px' }"
      >
        <div class="flex flex-col items-center">
          <svg width="120" height="36" viewBox="0 0 120 36" fill="none">
            <rect x="10" y="20" width="100" height="6" rx="3" fill="#475569" />
            <rect x="20" y="16" width="80" height="8" rx="2" fill="#64748b" />
            <rect x="30" y="24" width="4" height="10" fill="#475569" />
            <rect x="86" y="24" width="4" height="10" fill="#475569" />
            <circle cx="40" cy="19" r="2" fill="#22d3ee" opacity="0.8" />
            <circle cx="60" cy="19" r="2" fill="#22d3ee" opacity="0.8" />
            <circle cx="80" cy="19" r="2" fill="#22d3ee" opacity="0.8" />
          </svg>
          <div class="px-4 py-1 rounded-full bg-cyan-950 text-cyan-300 text-xs font-bold border border-cyan-700/50 mt-1">
            LAUNCH PAD
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue';
import SpaceNode from './SpaceNode.vue';
import SpaceConstellation from './SpaceConstellation.vue';
import SpaceRocket from './SpaceRocket.vue';

const props = defineProps({
  units: { type: Array, required: true }
});

defineEmits(['select-lesson']);

const spaceContainer = ref(null);
const animatedOffset = ref('999');
const containerWidth = ref(800);

const leftPercent = ref(20);
const rightPercent = ref(80);

function updateDimensions() {
  const w = window.innerWidth;
  containerWidth.value = w;
  if (w < 640) {
    leftPercent.value = 18;
    rightPercent.value = 82;
  } else if (w < 1024) {
    leftPercent.value = 22;
    rightPercent.value = 78;
  } else {
    leftPercent.value = 25;
    rightPercent.value = 75;
  }
}

const NODE_SPACING_Y = 110;
const CONSTELLATION_HEIGHT = 70;
const TOP_PADDING = 120;
const BOTTOM_PADDING = 80;

const nodePositions = computed(() => {
  const positions = [];
  let globalIndex = 0;

  for (const unit of props.units) {
    for (let i = 0; i < unit.lessons.length; i++) {
      const GROUP_SIZE = 3;
      const groupIndex = Math.floor(globalIndex / GROUP_SIZE);
      const posInGroup = globalIndex % GROUP_SIZE;
      const isLeft = groupIndex % 2 === 0;
      const basePct = isLeft ? leftPercent.value : rightPercent.value;
      const spreadDir = isLeft ? 1 : -1;
      const pct = basePct + posInGroup * 4 * spreadDir;
      positions.push({
        lesson: unit.lessons[i],
        unitId: unit.id,
        globalIndex,
        xPercent: pct,
        xPx: (pct / 100) * containerWidth.value,
        isFirstInUnit: i === 0,
        isLastInUnit: i === unit.lessons.length - 1
      });
      globalIndex++;
    }
  }

  const unitCount = props.units.length;
  const rawTotalHeight = positions.length * NODE_SPACING_Y + unitCount * CONSTELLATION_HEIGHT + TOP_PADDING + BOTTOM_PADDING;

  let currentY = rawTotalHeight - BOTTOM_PADDING;
  let unitIdx = 0;

  for (let i = 0; i < positions.length; i++) {
    positions[i].y = currentY;
    currentY -= NODE_SPACING_Y;

    if (positions[i].isLastInUnit && unitIdx < unitCount - 1) {
      currentY -= CONSTELLATION_HEIGHT;
      unitIdx++;
    }
  }

  return positions;
});

const constellationPositions = computed(() => {
  const markers = [];
  for (let i = 0; i < nodePositions.value.length; i++) {
    const node = nodePositions.value[i];
    if (node.isLastInUnit && i < nodePositions.value.length - 1) {
      const nextNode = nodePositions.value[i + 1];
      markers.push({
        unitId: nextNode.unitId,
        title: props.units.find(u => u.id === nextNode.unitId)?.title || '',
        y: (node.y + nextNode.y) / 2 - 15,
        xPercent: (node.xPercent + nextNode.xPercent) / 2
      });
    }
  }
  return markers;
});

const totalHeight = computed(() => {
  if (!nodePositions.value.length) return 400;
  const unitCount = props.units.length;
  return nodePositions.value.length * NODE_SPACING_Y + unitCount * CONSTELLATION_HEIGHT + TOP_PADDING + BOTTOM_PADDING;
});

const firstNodePercent = computed(() => nodePositions.value[0]?.xPercent || 50);
const firstNodeY = computed(() => nodePositions.value[0]?.y || totalHeight.value - BOTTOM_PADDING);
const lastNodePercent = computed(() => nodePositions.value[nodePositions.value.length - 1]?.xPercent || 50);
const lastNodeY = computed(() => nodePositions.value[nodePositions.value.length - 1]?.y || TOP_PADDING);

const currentNodePosition = computed(() => {
  return nodePositions.value.find(n => n.lesson.status === 'current') || null;
});

const flightPath = computed(() => {
  const nodes = nodePositions.value;
  if (nodes.length < 2) return '';

  let d = `M ${nodes[0].xPx} ${nodes[0].y}`;
  for (let i = 1; i < nodes.length; i++) {
    const prev = nodes[i - 1];
    const curr = nodes[i];
    const midY = (prev.y + curr.y) / 2;
    d += ` C ${prev.xPx} ${midY}, ${curr.xPx} ${midY}, ${curr.xPx} ${curr.y}`;
  }
  return d;
});

const completedPath = computed(() => {
  const nodes = nodePositions.value;
  // Extend solid line to include the current lesson (not just completed)
  const lastActive = nodes.reduce((last, n, i) => (n.lesson.status === 'completed' || n.lesson.status === 'current') ? i : last, -1);
  if (lastActive < 0) return '';
  if (lastActive === 0 && nodes.length > 1) {
    return `M ${nodes[0].xPx} ${nodes[0].y} L ${nodes[0].xPx} ${nodes[0].y}`;
  }

  const end = lastActive + 1;
  let d = `M ${nodes[0].xPx} ${nodes[0].y}`;
  for (let i = 1; i < end; i++) {
    const prev = nodes[i - 1];
    const curr = nodes[i];
    const midY = (prev.y + curr.y) / 2;
    d += ` C ${prev.xPx} ${midY}, ${curr.xPx} ${midY}, ${curr.xPx} ${curr.y}`;
  }
  return d;
});

const completedPathLength = computed(() => {
  const nodes = nodePositions.value;
  const lastActive = nodes.reduce((last, n, i) => (n.lesson.status === 'completed' || n.lesson.status === 'current') ? i : last, -1);
  if (lastActive < 1) return '0';
  let len = 0;
  for (let i = 1; i <= lastActive; i++) {
    const dx = nodes[i].xPx - nodes[i - 1].xPx;
    const dy = nodes[i].y - nodes[i - 1].y;
    len += Math.sqrt(dx * dx + dy * dy) * 1.3;
  }
  return String(Math.round(len));
});

// Background twinkling stars — more stars, spread across full height
const bgStars = computed(() => {
  const result = [];
  const h = totalHeight.value;
  for (let i = 0; i < 150; i++) {
    const seed = i * 31 + 7;
    result.push({
      id: i,
      x: (seed * 17) % 100,
      y: (seed * 53 + i * 41) % h,
      size: 1 + (seed % 3),
      delay: (seed % 50) / 10,
      duration: 2 + (seed % 30) / 10
    });
  }
  return result;
});

// Nebula clouds
const nebulae = computed(() => {
  const w = containerWidth.value;
  const h = totalHeight.value;
  const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#06b6d4', '#3b82f6'];
  const result = [];
  for (let i = 0; i < 8; i++) {
    const seed = i * 23 + 11;
    result.push({
      id: i,
      x: (seed * 37 + i * 97) % w,
      y: (seed * 67 + i * 131) % h,
      rx: 80 + (seed % 100),
      ry: 40 + (seed % 50),
      color: colors[seed % colors.length]
    });
  }
  return result;
});

onMounted(() => {
  updateDimensions();
  window.addEventListener('resize', updateDimensions);

  nextTick(() => {
    animatedOffset.value = completedPathLength.value;
    requestAnimationFrame(() => {
      animatedOffset.value = '0';
    });
  });

  nextTick(() => {
    setTimeout(() => {
      const currentEl = spaceContainer.value?.querySelector('[data-lesson-status="current"]');
      if (currentEl) {
        currentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', updateDimensions);
});
</script>

<style scoped>
.space-map-container {
  max-height: calc(100vh - 140px);
  scrollbar-width: thin;
  background: linear-gradient(
    to bottom,
    #020617 0%,
    #0f172a 30%,
    #1e1b4b 60%,
    #0f172a 85%,
    #020617 100%
  );
}

.space-map-container::-webkit-scrollbar {
  width: 6px;
}

.space-map-container::-webkit-scrollbar-thumb {
  background: #4338ca;
  border-radius: 3px;
}

.twinkle-star {
  animation: twinkle ease-in-out infinite alternate;
}

@keyframes twinkle {
  0% { opacity: 0.15; transform: scale(0.8); }
  100% { opacity: 1; transform: scale(1.3); }
}

.flight-completed-line {
  transition: stroke-dashoffset 1.5s ease-out;
}
</style>
